import * as THREE from "three";
import type { QualityTier } from "@engine";

// SkyTunnel renders a fullscreen "warp drive" backdrop behind the tunnel.
// Star streaks radiate from screen center, scroll outward with the
// player's forward speed, swirl gently around it as you go faster, and
// pulse on the audio low band (kick drum). It's a single screen-space
// pass with no geometry behind it -- so it costs one fullscreen quad
// per frame and gracefully degrades to a soft radial gradient on `low`
// quality.
//
// The sky deliberately does NOT follow the world group's rotation. In
// MORPH-HEX the world spins around the player on each input snap, but
// the camera orientation never changes -- so coupling the sky to that
// snap angle made it "shake" on every rotation. Instead, the sky owns
// its own continuous swirl driven by speed, so the tunnel always feels
// like it's rolling smoothly forward regardless of what the player
// inputs.
//
// Mounting strategy: a PlaneGeometry(2, 2) attached to scene.scene with
//   - depthTest = false, depthWrite = false  (never occludes anything)
//   - frustumCulled = false                  (always visible)
//   - renderOrder = -10000                   (drawn first)
// The vertex shader pins gl_Position.z = 0.999 so the quad sits at the
// far plane -- subsequent geometry overwrites it via the color buffer
// and we don't fight z-buffer at all.

const VERT = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    // PlaneGeometry(2, 2) range is -1..1, which is exactly NDC. We set
    // z to 0.999 so the quad lives just inside the far plane. This is
    // an old GPU-friendly trick -- no matrix math needed.
    gl_Position = vec4(position.xy, 0.999, 1.0);
  }
`;

const FRAG = /* glsl */ `
  precision highp float;

  varying vec2 vUv;

  uniform float uTime;
  uniform float uRotation;
  uniform float uSpeed;     // 0..1 -- 0 idle / 1 max warp
  uniform float uPulse;     // audio low band 0..1
  uniform vec3  uTintA;     // primary star tint
  uniform vec3  uTintB;     // secondary star tint
  uniform float uDensity;   // ~32..96 angular slots
  uniform float uDetail;    // 1.0 = full streaks, 0.0 = gradient only
  uniform vec2  uAspect;    // (1, h/w) so streaks are circular not oval

  // Cheap deterministic 1D hash. Used per-angular-slot to vary streak
  // start phase and radial speed so they don't all blink together.
  float hash11(float n) {
    return fract(sin(n * 127.1) * 43758.5453);
  }

  void main() {
    // Centred coords with aspect compensation so streaks fan out in a
    // circle instead of an ellipse on widescreen viewports.
    vec2 uv = (vUv - 0.5) * 2.0 * uAspect;

    float r = length(uv);
    float theta = atan(uv.y, uv.x) + uRotation;

    // Soft vignette gradient that's used both for the gradient fallback
    // AND as the base layer behind the streaks.
    float vignette = smoothstep(1.6, 0.0, r);
    vec3 grad = mix(uTintA, uTintB, smoothstep(0.0, 1.4, r)) * 0.18 * vignette;

    if (uDetail < 0.5) {
      // Low-quality fallback: just the gradient, no streak loop.
      gl_FragColor = vec4(grad, 1.0);
      return;
    }

    // Wrap angle into [0,1) and quantise into uDensity angular slots so
    // each slot owns one streak. Hash gives the slot a per-frame stable
    // identity for color + speed variance.
    float a01 = (theta / 6.2831853) + 0.5;
    float slot = floor(a01 * uDensity);
    float seed = hash11(slot);
    float seed2 = hash11(slot + 17.0);

    // Streaks scroll OUTWARD at a per-slot speed that scales with uSpeed
    // and audio pulse. fract() gives them a repeating birth-to-death
    // cycle: 0 at center, 1 at the edge.
    float streakSpeed = 0.18 + seed * 0.45 + uSpeed * 0.55 + uPulse * 0.4;
    float radialPhase = fract(uTime * streakSpeed + seed * 1.7);

    // The streak occupies a thin radial window around radialPhase. We
    // turn (r - radialPhase) into a normalised distance and shape it
    // with a smoothstep into a tight bright band.
    float radialDelta = r - radialPhase;
    float band = smoothstep(0.06, 0.0, abs(radialDelta));

    // Angular taper: a sin around the slot center so the streak isn't a
    // hard wedge but a tapered comet head.
    float slotCenter = (slot + 0.5) / uDensity;
    float angDist = a01 - slotCenter;
    angDist -= floor(angDist + 0.5);
    float taper = smoothstep(0.5 / uDensity, 0.0, abs(angDist));

    // Far-from-center stars are brighter (warp speed feel). Combined
    // with band+taper this isolates a clean comet streak per slot.
    float intensity = band * taper * smoothstep(0.05, 0.95, radialPhase);

    // Per-slot color tint between A and B. Audio pulse pushes brightness.
    vec3 starTint = mix(uTintA, uTintB, seed2);
    float boost = 1.4 + uPulse * 1.6 + uSpeed * 0.6;
    vec3 streak = starTint * intensity * boost;

    gl_FragColor = vec4(grad + streak, 1.0);
  }
`;

// Continuous swirl rate (radians/sec) at idle and at full warp. The
// sky always rolls a little so the tunnel feels alive even when the
// player is moving slowly; at full speed it picks up to give a sense
// of rotational momentum without ever snapping.
const SWIRL_RATE_IDLE = 0.05;
const SWIRL_RATE_MAX = 0.22;

export class SkyTunnel {
  private mesh: THREE.Mesh;
  private material: THREE.ShaderMaterial;
  private elapsed = 0;
  private swirlAngle = 0;

  constructor(tier: QualityTier = "high") {
    const geometry = new THREE.PlaneGeometry(2, 2);

    this.material = new THREE.ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: FRAG,
      depthTest: false,
      depthWrite: false,
      transparent: false,
      uniforms: {
        uTime: { value: 0 },
        uRotation: { value: 0 },
        uSpeed: { value: 0 },
        uPulse: { value: 0 },
        uTintA: { value: new THREE.Color(0x2bd3ff) },
        uTintB: { value: new THREE.Color(0xff5cb1) },
        uDensity: { value: 56 },
        uDetail: { value: tier === "low" ? 0 : 1 },
        uAspect: { value: new THREE.Vector2(1, 1) },
      },
    });

    this.mesh = new THREE.Mesh(geometry, this.material);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = -10000;
    this.mesh.name = "SkyTunnel";
  }

  attach(scene: THREE.Scene): void {
    scene.add(this.mesh);
  }

  detach(): void {
    this.mesh.parent?.remove(this.mesh);
  }

  setTheme(tintA: number, tintB: number, density: number): void {
    (this.material.uniforms["uTintA"]!.value as THREE.Color).setHex(tintA);
    (this.material.uniforms["uTintB"]!.value as THREE.Color).setHex(tintB);
    this.material.uniforms["uDensity"]!.value = Math.max(8, density);
  }

  setAspect(width: number, height: number): void {
    const v = this.material.uniforms["uAspect"]!.value as THREE.Vector2;
    if (width >= height) {
      v.set(1, height / Math.max(1, width));
    } else {
      v.set(width / Math.max(1, height), 1);
    }
  }

  // Note: worldRotZ is intentionally NOT consumed. The sky only rolls
  // via its own swirl accumulator so input-driven world snaps don't
  // bleed into the backdrop.
  update(
    unscaledDt: number,
    _worldRotZ: number,
    speed01: number,
    audioLow: number
  ): void {
    this.elapsed += unscaledDt;
    const s = Math.max(0, Math.min(1, speed01));
    const swirlRate = SWIRL_RATE_IDLE + (SWIRL_RATE_MAX - SWIRL_RATE_IDLE) * s;
    this.swirlAngle += swirlRate * unscaledDt;
    this.material.uniforms["uTime"]!.value = this.elapsed;
    this.material.uniforms["uRotation"]!.value = this.swirlAngle;
    this.material.uniforms["uSpeed"]!.value = s;
    this.material.uniforms["uPulse"]!.value = Math.max(0, Math.min(1, audioLow));
  }

  dispose(): void {
    this.detach();
    this.mesh.geometry.dispose();
    this.material.dispose();
  }
}
